import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Avatar, Modal } from '../../components';
import { useFileUpload } from '../../components/hooks/useFileUpload';
import { ImageCropper } from '../../components/ImageCropper/ImageCropper';
import { updateUser } from '../../store/users/users.actions';
import { RootState } from '../../store/store';
import Label from '../../components/Label/Label';
import { EditUserName } from './components/editName/EditUserName';
import { EditUserJobTitle } from './components/editJobTitle/EditUserJobTitle';
import { EditUserTelNumber } from './components/editPhoneNumber/EditUserTelNumber';
import { EditTimeZone } from './components/editTimeZone/EditTimeZone';
import { layoutImg, qBeta } from './icons';
import { ChangeUserPassword } from './components/changePassword/ChangeUserPassword';
import { getUserData } from '../../store/auth/auth.actions';

export const Account: React.FC = (): JSX.Element => {
  const {
    handleUpload,
    file,
    removeFile,
    croppedImage,
    saveCroppedImage,
  } = useFileUpload();

  const { user, org } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  const [isOpenRename, setIsOpenRename] = useState(false);
  const [isOpenChangePassword, setisOpenChangePassword] = useState(false);
  const [isOpenEditJob, setIsOpenEditJob] = useState(false);
  const [isOpenEditPhone, setIsOpenEditPhone] = useState(false);
  const [isOpenEditTimeZone, setIsOpenEditTimeZone] = useState(false);

  useEffect(() => {
    if (user) dispatch(getUserData(user.orgId, user.userId));
  }, []);

  useEffect(() => {
    if (croppedImage)
      dispatch(
        updateUser(
          { avatar: croppedImage },
          user?.orgId || '',
          user?.userId || ''
        )
      );
  }, [croppedImage, dispatch, user?.userId, user?.orgId]);

  const conditionToShowGMT: string = user?.timezone?.includes('GMT')
    ? user.timezone
    : `GMT${user?.timezone}`;

  const signInProvider = user?.signupProviders.find(
    (el) => el?.metadata?.default
  );
  const permissionToChangePswdAndName =
    signInProvider?.name === 'substrata' ?? false;

  return (
    <div className="account" id="account">
      <Modal
        open={!!file}
        onClose={removeFile}
        title="Upload a photo"
        height="570px"
      >
        <ImageCropper
          onSave={saveCroppedImage}
          onCancel={removeFile}
          image={file || ''}
        />
      </Modal>
      <EditUserName
        open={isOpenRename}
        onClose={() => setIsOpenRename(false)}
      />
      <ChangeUserPassword
        open={isOpenChangePassword}
        onClose={() => setisOpenChangePassword(false)}
      />
      <EditUserJobTitle
        open={isOpenEditJob}
        onClose={() => setIsOpenEditJob(false)}
      />
      <EditUserTelNumber
        open={isOpenEditPhone}
        onClose={() => setIsOpenEditPhone(false)}
      />
      <EditTimeZone
        open={isOpenEditTimeZone}
        onClose={() => setIsOpenEditTimeZone(false)}
      />
      <div className="account__profile">
        <div className="account__profile-avatar">
          <Avatar
            user={user}
            image={user?.avatar}
            className="account__profile-avatar__image"
            onUpload={handleUpload}
            hasUpload
            hasActivityStatus
          />
          <div className="account__profile-avatar__role">{user?.role}</div>
        </div>
        <div className="account__profile-content">
          <div className="account__profile-content-firstcolumn">
            <Label
              title="NAME"
              value={`${user?.firstName} ${user?.lastName}`}
              onClick={() => setIsOpenRename(true)}
              tooltip="If you'd like to change your name, 
              please do by going to your settings in your Google account"
              permission={permissionToChangePswdAndName}
              icon={!permissionToChangePswdAndName}
            />
            <Label
              title="EMAIL"
              value={user?.email}
              tooltip="Email cannot be changed"
              icon
            />
            <Label
              title="PASSWORD"
              value="•••••••••••••••••••••"
              onClick={() => setisOpenChangePassword(true)}
              style={{ marginLeft: 70 }}
              tooltip="If you'd like to change your password, 
              please do by going to your settings in your Google account"
              permission={permissionToChangePswdAndName}
              icon={!permissionToChangePswdAndName}
            />
          </div>
          <div className="account__profile-content-secondcolumn">
            <Label
              title="JOB TITLE"
              value={user?.title}
              onClick={() => setIsOpenEditJob(true)}
            />
            <Label
              title="PHONE"
              value={user?.phoneNumber}
              onClick={() => setIsOpenEditPhone(true)}
            />
            <Label
              title="TIMEZONE"
              value={conditionToShowGMT}
              onClick={() => setIsOpenEditTimeZone(true)}
            />
          </div>
          <div className="account__profile-content-thirdColumn">
            <div className="account__profile-content-thirdColumn__info">
              <span className="account__profile-content-thirdColumn__info-start">
                Last sign in:
              </span>{' '}
              {moment(user?.lastSignIn).fromNow()}
            </div>
            <div className="account__profile-content-thirdColumn__info">
              <span className="account__profile-content-thirdColumn__info-start">
                Member since:
              </span>{' '}
              {moment(user?.createdAt).format('MMM D, YYYY')}
            </div>
            <div className="account__profile-content-thirdColumn__email">
              Total emails analyzed:{' '}
              <span className="account__profile-content-thirdColumn__email-bold">
                Soon
              </span>
            </div>
            <div className="account__profile-content-thirdColumn__info">
              <span className="account__profile-content-thirdColumn__info-start">
                Organizational Unit:
              </span>{' '}
              <span>
                {org?.avatar ? (
                  <img
                    className="account__profile-content-thirdColumn__info-logo"
                    src={org.avatar}
                    alt="Logo"
                  />
                ) : null}{' '}
                {org?.name}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="account__footer">
        <img src={layoutImg} alt="img" className="account__footer-img" />
        <div className="account__footer-personal">
          <span className="account__footer-personal__text">
            Check out your Personal Q page
          </span>

          <label
            htmlFor="pesonalQ"
            className="account__footer-personal__button"
          >
            <Link to={`/orgs/${user?.orgId}/users/${user?.userId}/personalq`}>
              <span className="account__footer-personal__button-qImg">
                <img src={qBeta} alt="Q-beta" />
              </span>
              <span className="account__footer-personal__button-text">
                Personal Q
              </span>
            </Link>
          </label>

          <span id="pesonalQ" />
        </div>
      </div>
    </div>
  );
};
